import { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  engagementTypeLabel,
  MEETING_REPEAT_LABEL,
  MEETING_RESPONSE_LABEL,
  MEETING_STATUS_LABEL,
  type CalendarEventSummary,
  type MeetingResponse,
} from '@todo/shared';

/**
 * The Teams-style peek card: click a chip and the meeting opens right
 * beside it - when, where, who invited you, how the room answered, and the
 * RSVP buttons. Everything an invitee needs without leaving the grid.
 *
 * The side panel is still there for the things that need room: editing an
 * event you own, and the full detail read. The expand arrow goes there.
 */

/** Where the card should sit: the chip's rectangle in viewport coords. */
export interface PopoverAnchor {
  top: number;
  left: number;
  width: number;
  height: number;
}

const CARD_W = 400;
const CARD_MAX_H = 460;
const GAP = 8;

/**
 * BESIDE the chip, never on top of it: the card opens to the RIGHT, flips
 * to the LEFT when the right edge has no room, and only falls back to
 * below/above on a screen too narrow for either side. That keeps the chip
 * you clicked - and the hours around it - visible while you read the card,
 * which is the whole point of a peek.
 *
 * Vertically it lines up with the chip's top, then slides up just enough to
 * stay on screen. Viewport coordinates throughout, so the card is
 * `position: fixed` and does not care what is scrolled underneath it.
 */
function place(anchor: PopoverAnchor): { top: number; left: number } {
  const roomRight = window.innerWidth - (anchor.left + anchor.width) - GAP * 2;
  const roomLeft = anchor.left - GAP * 2;

  let left: number;
  if (roomRight >= CARD_W) {
    left = anchor.left + anchor.width + GAP;
  } else if (roomLeft >= CARD_W) {
    left = anchor.left - CARD_W - GAP;
  } else {
    // Neither side fits - centre it and let the vertical rules below place it.
    left = Math.min(
      Math.max(GAP, anchor.left + anchor.width / 2 - CARD_W / 2),
      Math.max(GAP, window.innerWidth - CARD_W - GAP),
    );
  }

  // Start level with the chip, then clamp inside the viewport.
  const top = Math.min(
    Math.max(GAP, anchor.top),
    Math.max(GAP, window.innerHeight - CARD_MAX_H - GAP),
  );
  return { top, left };
}

function timeRange(startsAt: string, endsAt: string): string {
  const start = new Date(startsAt);
  const end = new Date(endsAt);
  const day = start.toLocaleDateString(undefined, {
    weekday: 'short', day: '2-digit', month: 'short', year: 'numeric',
  });
  const t = (d: Date) => d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  return `${day} ${t(start)} - ${t(end)}`;
}

const RESPONSE_CHIP: Record<MeetingResponse, string> = {
  PENDING: 'bg-slate-100 text-slate-500',
  ACCEPTED: 'bg-emerald-100 text-emerald-800',
  DECLINED: 'bg-red-100 text-red-700',
  TENTATIVE: 'bg-amber-100 text-amber-800',
};

function Row({ icon, children }: { icon: ReactNode; children: ReactNode }) {
  return (
    <div className="flex items-start gap-2.5 border-t border-slate-100 px-4 py-2.5 first:border-t-0">
      <span className="mt-0.5 w-4 shrink-0 text-center text-slate-400" aria-hidden>{icon}</span>
      <div className="min-w-0 flex-1 text-sm text-slate-700">{children}</div>
    </div>
  );
}

export function MeetingPopover({
  event, anchor, busy, onRespond, onExpand, onClose,
  onToggleChecklistItem, onAddChecklistItem, onRemoveChecklistItem,
}: {
  event: CalendarEventSummary;
  anchor: PopoverAnchor;
  busy: boolean;
  onRespond: (response: Exclude<MeetingResponse, 'PENDING'>) => void;
  /** Open the side panel - editing for an owner, full detail otherwise. */
  onExpand: () => void;
  onClose: () => void;
  /** Engagement prep, organiser only. Omitted for meetings. */
  onToggleChecklistItem?: (itemId: string, done: boolean) => void;
  onAddChecklistItem?: (label: string) => void;
  onRemoveChecklistItem?: (itemId: string) => void;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [newItem, setNewItem] = useState('');
  const { top, left } = place(anchor);

  /**
   * Escape, an outside click, ANY scroll, or a resize closes the card.
   *
   * Scroll matters: the card is anchored to a rectangle captured at click
   * time, so the moment the grid moves underneath, that rectangle is a lie
   * and the card would float beside the wrong hour. Closing is honest and
   * costs one click to reopen; re-measuring on every scroll frame is not
   * worth it for a peek. The listener is CAPTURING so it also sees scrolls
   * inside the week planner, which do not bubble to the window.
   */
  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose(); }
    function onDown(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    }
    function onScroll(e: Event) {
      // A scroll INSIDE the card (a long attendee list) must not close it.
      if (ref.current && e.target instanceof Node && ref.current.contains(e.target)) return;
      onClose();
    }
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onClose);
    // Deferred: the click that OPENED the card must not immediately close it.
    const timer = setTimeout(() => document.addEventListener('mousedown', onDown), 0);
    return () => {
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onClose);
      document.removeEventListener('mousedown', onDown);
      clearTimeout(timer);
    };
  }, [onClose]);

  const accepted = event.attendees.filter((a) => a.response === 'ACCEPTED').length;
  const declined = event.attendees.filter((a) => a.response === 'DECLINED').length;
  const tentative = event.attendees.filter((a) => a.response === 'TENTATIVE').length;
  const pending = event.attendees.filter((a) => a.response === 'PENDING').length;

  const iDeclined = event.myResponse === 'DECLINED';
  const isEngagement = event.kind === 'ENGAGEMENT';
  const doneCount = event.checklist.filter((c) => c.done).length;

  return (
    <div
      ref={ref}
      role="dialog"
      aria-label={event.title}
      style={{ top, left, width: CARD_W, maxHeight: CARD_MAX_H }}
      className="fixed z-50 overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-2xl"
    >
      {/* A coloured rail at the top, the way Teams marks the calendar it
          came from - here it marks whether this is still a commitment. */}
      <div className={[
        'h-1 w-full',
        event.status === 'CANCELLED' || iDeclined
          ? 'bg-red-500'
          : isEngagement ? 'bg-violet-600' : 'bg-brand-600',
      ].join(' ')} />

      <div className="flex items-start justify-between gap-2 px-4 pt-3">
        <h2 className={[
          'text-base font-semibold text-slate-900',
          event.status === 'CANCELLED' || iDeclined ? 'line-through opacity-70' : '',
        ].join(' ')}>
          {event.status === 'CANCELLED' ? `Cancelled: ${event.title}` : event.title}
        </h2>
        <div className="flex shrink-0 items-center gap-1">
          <button type="button" onClick={onExpand} aria-label="Open full details"
            title={event.isOwner ? 'Edit this meeting' : 'Open full details'}
            className="rounded-md px-1.5 py-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600">
            ⤢
          </button>
          <button type="button" onClick={onClose} aria-label="Close"
            className="rounded-md px-1.5 py-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600">
            ×
          </button>
        </div>
      </div>

      {iDeclined && (
        <p className="mx-4 mt-2 rounded-lg bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-700">
          You declined this meeting.
        </p>
      )}
      {event.hasConflict && !iDeclined && (
        <p className="mx-4 mt-2 rounded-lg bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-800">
          Overlaps another meeting on your calendar.
        </p>
      )}

      <div className="mt-3">
        <Row icon="🕐">
          <span className="flex flex-wrap items-center gap-2">
            {timeRange(event.startsAt, event.endsAt)}
            {event.repeat !== 'NONE' && (
              <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px] font-medium text-slate-500">
                {MEETING_REPEAT_LABEL[event.repeat]}
              </span>
            )}
            <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px] font-medium text-slate-500">
              {MEETING_STATUS_LABEL[event.status]}
            </span>
          </span>
        </Row>

        {event.location && <Row icon="📍">{event.location}</Row>}

        <Row icon="👤">
          <p>
            {event.isOwner
              ? isEngagement ? 'You organise this engagement.' : 'You organise this meeting.'
              : isEngagement
                ? <><span className="font-medium">{event.ownerName}</span> is organising this.</>
                : <><span className="font-medium">{event.ownerName}</span> invited you.</>}
          </p>
          {/* Teams' one-line tally - the answer to "who is actually coming".
              An engagement has no invitations, so it has no tally. */}
          {!isEngagement && event.attendees.length > 0 && (
            <p className="mt-0.5 text-xs text-slate-500">
              Accepted {accepted} · Declined {declined} · Not sure {tentative} · No answer {pending}
            </p>
          )}
        </Row>

        {event.attendees.length > 0 && (
          <Row icon="👥">
            {isEngagement && (
              <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">
                Staff
              </p>
            )}
            <ul className="space-y-1">
              {event.attendees.map((a) => (
                <li key={a.userId} className="flex items-center justify-between gap-2">
                  <span className="truncate text-sm">{a.displayName}</span>
                  {/* No RSVP chip on an engagement - nobody was invited. */}
                  {!isEngagement && (
                    <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold ${RESPONSE_CHIP[a.response]}`}>
                      {MEETING_RESPONSE_LABEL[a.response]}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </Row>
        )}

        {event.description && (
          <Row icon="📝">
            <p className="line-clamp-4 whitespace-pre-wrap text-sm text-slate-600">
              {event.description}
            </p>
          </Row>
        )}
      </div>

      {isEngagement && (
        <>
          <Row icon="📣">
            <span className="flex flex-wrap items-center gap-2">
              <span className="rounded bg-violet-100 px-1.5 py-0.5 text-[11px] font-semibold text-violet-800">
                {engagementTypeLabel(event.engagementType)}
              </span>
              {event.expectedAttendance !== null && (
                <span className="text-xs text-slate-500">
                  ~{event.expectedAttendance.toLocaleString('en-IN')} expected
                </span>
              )}
            </span>
          </Row>

          <Row icon="✅">
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Prep checklist
              </span>
              <span className="text-xs font-semibold text-slate-500">
                {doneCount}/{event.checklist.length}
              </span>
            </div>

            {/* A plain progress bar - the one number an organiser wants
                from across the room. */}
            {event.checklist.length > 0 && (
              <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-slate-200">
                <div className="h-full rounded-full bg-violet-500 transition-all"
                  style={{ width: `${(doneCount / event.checklist.length) * 100}%` }} />
              </div>
            )}

            <ul className="mt-2 space-y-1">
              {event.checklist.map((item) => (
                <li key={item.id} className="group flex items-start gap-2">
                  <input type="checkbox" checked={item.done}
                    disabled={!event.isOwner || !onToggleChecklistItem}
                    onChange={(e) => onToggleChecklistItem?.(item.id, e.target.checked)}
                    className="mt-0.5 h-3.5 w-3.5 shrink-0 rounded border-slate-300 text-violet-600 focus:ring-violet-500 disabled:opacity-60" />
                  <span className={[
                    'min-w-0 flex-1 text-sm',
                    item.done ? 'text-slate-400 line-through' : 'text-slate-700',
                  ].join(' ')}>
                    {item.label}
                  </span>
                  {event.isOwner && onRemoveChecklistItem && (
                    <button type="button" aria-label={`Remove ${item.label}`}
                      onClick={() => onRemoveChecklistItem(item.id)}
                      className="shrink-0 text-slate-300 opacity-0 transition hover:text-red-500 group-hover:opacity-100">
                      ×
                    </button>
                  )}
                </li>
              ))}
              {event.checklist.length === 0 && (
                <li className="text-xs text-slate-400">Nothing on the list yet.</li>
              )}
            </ul>

            {event.isOwner && onAddChecklistItem && (
              <form className="mt-2 flex gap-1.5"
                onSubmit={(e) => {
                  e.preventDefault();
                  const label = newItem.trim();
                  if (!label) return;
                  onAddChecklistItem(label);
                  setNewItem('');
                }}>
                <input value={newItem} onChange={(e) => setNewItem(e.target.value)}
                  placeholder="Add a step"
                  className="min-w-0 flex-1 rounded-lg border border-slate-300 px-2 py-1 text-xs outline-none transition focus:border-violet-500" />
                <button type="submit" disabled={!newItem.trim()}
                  className="shrink-0 rounded-lg bg-violet-600 px-2.5 py-1 text-xs font-semibold text-white transition hover:bg-violet-700 disabled:opacity-50">
                  Add
                </button>
              </form>
            )}
          </Row>
        </>
      )}

      {/* RSVP - invitees only, and not on a meeting that is already off. */}
      {event.myResponse && event.status !== 'CANCELLED' && (
        <div className="flex items-center gap-1.5 border-t border-slate-100 px-4 py-3">
          {(['ACCEPTED', 'TENTATIVE', 'DECLINED'] as const).map((r) => (
            <button key={r} type="button" disabled={busy}
              onClick={() => onRespond(r)}
              className={[
                'rounded-lg border px-3 py-1.5 text-xs font-semibold transition disabled:opacity-50',
                event.myResponse === r
                  ? r === 'DECLINED'
                    ? 'border-red-500 bg-red-50 text-red-700'
                    : r === 'TENTATIVE'
                      ? 'border-amber-500 bg-amber-50 text-amber-800'
                      : 'border-emerald-500 bg-emerald-50 text-emerald-800'
                  : 'border-slate-300 text-slate-600 hover:bg-slate-50',
              ].join(' ')}>
              {MEETING_RESPONSE_LABEL[r]}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
